/**
 * Types for the Referral Details → Updates tab activity feed (US 18295–18302).
 *
 * These mirror the backend integration document "Updates Feed & Notification Bell"
 * (branch `sprint_87_Notification_rework_BE`) — `POST /api/ActivityFeed/GetFeed`.
 *
 * Timestamps arrive as UTC **without a Z suffix**, so every one must go through
 * `parseApiUtc` rather than straight into dayjs.
 */

import { NotificationScope } from 'features/notifications/types/grouped'

/**
 * Feed entry categories, as returned in `category`. The label ships alongside as
 * `categoryDescription`, so prefer that over mapping these numbers for display.
 */
export enum ActivityCategory {
  Discussion = 1,
  ReferralSource = 2,
  Attachment = 3,
  Status = 4,
  Note = 5,
  ActivityLog = 6,
}

/** One message preview inside a Discussion entry. The array arrives newest first. */
export type DiscussionMessagePreview = {
  messageId: string
  senderUserId: string
  senderName: string
  message: string
  createdDate: string
  /** Sendbird custom type, e.g. "broadcast". */
  customType?: string | null
  /** Sendbird data as a raw JSON string; attachments carry `hasAttachment` and the doc `id`. */
  data?: string | null
  /** "users" or "channel" (@everyone). */
  mentionType?: string | null
  mentionedUsers?: { userId: string; nickname: string }[]
}

export type DiscussionMetadata = {
  channelUrl: string
  /** Null when the viewer is not a member of the channel. */
  messageCount: number | null
  /** False when the viewer is not in the channel; the card then carries no messages. */
  isParticipant?: boolean
  lastMessageId: string | null
  recentMessages: DiscussionMessagePreview[]
}

export type ReferralSourceMetadata = {
  isInbound: boolean
  communicationType: number
  referralSourceId: number | null
  /** Route description — "Fax", "Email". */
  referralSource: string | null
  referringHospital: string | null
}

export type AttachmentMetadata = {
  attachmentId: number
  fileName: string
  fileType: string | null
  /** Bytes. */
  size: number | null
  version: number | null
  description: string | null
}

export type StatusMetadata = {
  status: string
  statusId: number
  /**
   * The note typed while changing the status, when there was one (18299). The backend
   * returns it here rather than as a separate entry — category 6 produces nothing.
   */
  comment: string | null
  /** Denials only. */
  denialReason: string | null
  denialReasonId: number | null
}

export type NoteMetadata = {
  /** Display label, e.g. "Medium". */
  priority: string | null
  /** Numeric priority, on the same 0/1/2 scale as ReferralNotes. */
  priorityValue: number | null
  engagementTypeId: number | null
  engagementType: string | null
  /** True only when the caller wrote the note — drives the edit affordance. */
  canEdit: boolean
}

export type ActivityLogMetadata = {
  activityTypeId: number
  /** Display label. */
  activityType: string | null
  isDivider: boolean
}

/** Fields every feed entry carries, whatever its category. */
type ActivityFeedEntryBase = {
  /** Primary key in its own source table — unique per category, not across the feed. */
  sourceId: number
  occurredAt: string
  /** Null when the entry is referral-wide; notes and attachments always are. */
  patientFacilityId: number | null
  /** Only populated on Referral Source and Discussion; Status names its community in `body`. */
  communityName: string | null
  actorUserId: string | null
  actorDisplayName: string | null
  title: string | null
  body: string | null
  categoryDescription: string
}

/**
 * Discriminated union on `category`, so each card receives a narrowed `metadata` without
 * casting. Activity Log is category 6; the backend defines no Comment category.
 */
export type ActivityFeedEntry =
  | (ActivityFeedEntryBase & {
      category: ActivityCategory.Discussion
      metadata: DiscussionMetadata
    })
  | (ActivityFeedEntryBase & {
      category: ActivityCategory.ReferralSource
      metadata: ReferralSourceMetadata
    })
  | (ActivityFeedEntryBase & {
      category: ActivityCategory.Attachment
      metadata: AttachmentMetadata
    })
  | (ActivityFeedEntryBase & {
      category: ActivityCategory.Status
      metadata: StatusMetadata
    })
  | (ActivityFeedEntryBase & {
      category: ActivityCategory.Note
      metadata: NoteMetadata
    })
  | (ActivityFeedEntryBase & {
      category: ActivityCategory.ActivityLog
      metadata: ActivityLogMetadata
    })

/** Narrowed entry type for a given category, e.g. `EntryOf<ActivityCategory.Note>`. */
export type EntryOf<C extends ActivityCategory> = Extract<ActivityFeedEntry, { category: C }>

/** The selected role and communities narrow the feed the same way they narrow the bell. */
export type ActivityFeedRequest = Partial<NotificationScope> & {
  patientId: number
  /** Null requests the Patient Referral rollup across every accessible community. */
  patientFacilityId: number | null
  /** Empty or null means all categories. Activity Log returns regardless of the filter. */
  categories: ActivityCategory[] | null
  cursor: string | null
  /** Defaults to 25 server-side, capped at 100. */
  pageSize: number
}

export type ActivityFeedPage = {
  items: ActivityFeedEntry[]
  /** Opaque; pass back verbatim. A stale cursor silently returns page one. */
  nextCursor: string | null
  hasMore: boolean
}

export type ActivityFeedResponse = {
  data: {
    isSuccess: boolean
    message: string
    /** Items in THIS page, not a grand total — the feed has no total. */
    totalCount: number
    hasData: boolean
    data: ActivityFeedPage
  }
}
